// CommonUtil - Shared utility methods for template processing
// Universal version - works in both Node.js and browser environments

import { JsonObject } from '../app/json/jsonValue.js';
import { JsonUtility } from '../app/jsonUtility.js';
import { Logger } from '../../../Arshu/dist/logger/logger.js';
import { DynamicConfigMap } from '../registry/dynamicConfigMap.js';

export interface ComponentReuseResult {
    baseTemplateName: string;
    reuseJsonName: string | null;
    isReusePattern: boolean;
}

/**
 * One consuming client-handler declaration parsed from a component's rendered HTML (§2B.3) — the co-located
 * pair carried on a single element: data-client-handler (the registered IClient Name, e.g. ScenarioClient)
 * and data-client-handler-param (the handler's static param JSON, passed as the paramJson slot). The input
 * island key is DERIVED from the handler Name by convention — clientDataKey = <HandlerName>Data (so
 * ScenarioClient reads its ScenarioClientData preparer's island) — combined with the component name it keys
 * the input island <ComponentName>_<ClientDataKey>. A component may host several declarations (one per
 * client-data binding), each independent.
 */
export class ClientHandlerDeclaration {
    handlerName: string;
    clientDataKey: string;
    param: string;

    constructor(handlerName: string, clientDataKey: string, param: string) {
        this.handlerName = handlerName;
        this.clientDataKey = clientDataKey;
        this.param = param;
    }
}

export const CommonUtil = {
    isAlphaNumeric(str: string | null | undefined): boolean {
        if (!str || typeof str !== 'string') {
            return false;
        }

        for (let i = 0; i < str.length; i++) {
            const ch = str[i];
            const code = ch.charCodeAt(0);
            const isDigit = code >= 48 && code <= 57;
            const isUpper = code >= 65 && code <= 90;
            const isLower = code >= 97 && code <= 122;
            const isUnderscore = ch === '_';
            const isExclamation = ch === '!';
            if (!(isDigit || isUpper || isLower || isUnderscore || isExclamation)) {
                return false;
            }
        }
        return true;
    },

    findMatchingCloseTag(content: string, startPos: number, openTag: string, closeTag: string): number {
        let searchPos = startPos;
        let openCount = 1;

        while (searchPos < content.length && openCount > 0) {
            const nextOpen = content.indexOf(openTag, searchPos);
            const nextClose = content.indexOf(closeTag, searchPos);

            if (nextClose === -1) return -1;

            if (nextOpen !== -1 && nextOpen < nextClose) {
                openCount++;
                searchPos = nextOpen + openTag.length;
            } else {
                openCount--;
                if (openCount === 0) {
                    return nextClose;
                }
                searchPos = nextClose + closeTag.length;
            }
        }

        return -1;
    },

    removeRemainingSlotPlaceholders(html: string): string {
        let result = html;
        let searchPos = 0;

        while (searchPos < result.length) {
            const placeholderStart = result.indexOf('{{$HTMLPLACEHOLDER', searchPos);
            if (placeholderStart === -1) break;

            const afterPlaceholder = placeholderStart + 18;
            let pos = afterPlaceholder;

            while (pos < result.length) {
                const code = result.charCodeAt(pos);
                if (code >= 48 && code <= 57) {
                    pos++;
                } else {
                    break;
                }
            }

            if (pos + 1 < result.length && result.substring(pos, pos + 2) === '}}') {
                const placeholderEnd = pos + 2;
                const placeholder = result.substring(placeholderStart, placeholderEnd);
                result = result.replace(placeholder, '');
            } else {
                searchPos = placeholderStart + 1;
            }
        }

        return result;
    },

    replaceCaseInsensitive(text: string, from: string, to: string): string {
        if (!text || !from) return text;

        const index = text.toLowerCase().indexOf(from.toLowerCase());
        if (index >= 0) {
            return text.substring(0, index) + to + text.substring(index + from.length);
        }
        return text;
    },

    replaceAllFrom(text: string, fromIndex: number, from: string, to: string): string {
        if (!text || !from || fromIndex < 0) return text;
        return text.substring(0, fromIndex) + text.substring(fromIndex).replaceAll(from, to);
    },

    normalizeFileContent(content: string | null | undefined): string {
        if (!content || typeof content !== 'string') {
            return content || '';
        }

        let result = content;
        if (result.charCodeAt(0) === 0xFEFF) {
            result = result.slice(1);
        }

        result = result.replaceAll('\r\n', '\n').replaceAll('\r', '\n');
        return result;
    },

    utf16Len(str: string | null | undefined): number {
        if (!str || typeof str !== 'string') {
            return 0;
        }
        return str.length;
    },

    parseComponentReusePattern(placeholderName: string, currentTemplateName: string): ComponentReuseResult {
        if (!currentTemplateName || !placeholderName) {
            return { baseTemplateName: placeholderName, reuseJsonName: null, isReusePattern: false };
        }

        const parts = placeholderName.split('_');
        if (parts.length < 3) {
            return { baseTemplateName: placeholderName, reuseJsonName: null, isReusePattern: false };
        }

        const isAllDigits = (s: string): boolean => {
            if (!s || s.length === 0) return false;
            for (let j = 0; j < s.length; j++) {
                const code = s.charCodeAt(j);
                if (code < 48 || code > 57) return false;
            }
            return true;
        };

        for (let i = 1; i < parts.length - 1; i++) {
            if (isAllDigits(parts[i])) {
                const prefix = parts.slice(0, i).join('_');

                if (prefix.toLowerCase() === currentTemplateName.toLowerCase()) {
                    const baseTemplateName = parts.slice(i + 1).join('_');
                    const reuseJsonName = placeholderName;

                    return { baseTemplateName, reuseJsonName, isReusePattern: true };
                }
            }
        }

        return { baseTemplateName: placeholderName, reuseJsonName: null, isReusePattern: false };
    },

    isDynamicOrGlobalConfigName(contentName: string): boolean {
        const lower = contentName.toLowerCase();
        if (this.isDynamicOrGlobalConfigBaseName(lower)) {
            return true;
        }

        const lastUnderscore = lower.lastIndexOf('_');
        return lastUnderscore >= 0
            && lastUnderscore + 1 < lower.length
            && this.isDynamicOrGlobalConfigBaseName(lower.substring(lastUnderscore + 1));
    },

    isDynamicOrGlobalConfigBaseName(lower: string): boolean {
        return lower === 'dynamicdata' || lower.startsWith('dynamicdata.')
            || lower === 'dynamicclientdata' || lower.startsWith('dynamicclientdata.')
            || lower === 'dynamicview' || lower.startsWith('dynamicview.')
            || lower === 'dynamicaction' || lower.startsWith('dynamicaction.')
            || lower === 'dynamicappsite' || lower.startsWith('dynamicappsite.')
            || lower === 'globaldata'
            || lower === 'globalviewdata' || lower.startsWith('globalviewdata.');
    },

    buildAppSitesToLoad(appSite: string, searchAppSites: string): string[] {
        const appSitesToLoad = [appSite];
        if (!searchAppSites) {
            return appSitesToLoad;
        }

        const searchArray = searchAppSites.split(',');
        for (let i = 0; i < searchArray.length; i++) {
            const trimmed = searchArray[i].trim();
            if (!trimmed) {
                continue;
            }

            let found = false;
            for (let j = 0; j < appSitesToLoad.length; j++) {
                if (appSitesToLoad[j] === trimmed) {
                    found = true;
                    break;
                }
            }

            if (!found) {
                appSitesToLoad.push(trimmed);
            }
        }

        return appSitesToLoad;
    },

    /**
     * Narrows a dynamicclientdata.json binding map to its PURE bindings — the ones whose dataSource CSV
     * carries "Pure", meaning the iType names the IClient PROCESSOR and the handler derives its output from
     * its declared params alone.
     *
     * Only these bindings are shipped to the reconstruction sources (api.json / OCI). A pure handler runs on
     * the CLIENT, so — exactly like DynamicView, whose config and params have always shipped — the client
     * cannot execute it without its binding. The Embedded preparer bindings stay server-side: a browser can
     * never run an IClientData preparer, and its raw already ships as the delivered island.
     *
     * Returns null when the map carries no pure binding, so the caller serializes "null" and an AppSite
     * without a pure tier adds nothing to the wire.
     */
    filterPureClientBindings(dynamicConfig: DynamicConfigMap | null): DynamicConfigMap | null {
        if (!dynamicConfig) return null;

        let pureOnly: DynamicConfigMap | null = null;
        for (const [key, handlers] of dynamicConfig.entries()) {
            let pureHandlers: typeof handlers | null = null;
            for (const handler of handlers) {
                if (!handler.hasDataSource('Pure')) continue;
                if (pureHandlers === null) pureHandlers = [];
                pureHandlers.push(handler);
            }
            if (pureHandlers === null) continue;
            if (pureOnly === null) pureOnly = new DynamicConfigMap();
            pureOnly.set(key, pureHandlers);
        }

        return pureOnly;
    },

    /**
     * Narrows a client-data param store (keyed "{appSite}:{iParamFile}") to the entries a PURE binding
     * actually names, so the wire carries the params the client needs to run its pure handlers and nothing
     * more. Without this the Embedded PREPARER's params — server-side config the browser has no business
     * seeing — would ride along, since both tiers share one dynamicclientdata param store.
     */
    filterPureClientParams(
        clientDataParamsStore: JsonObject,
        allSites: string[],
        getClientDataConfig: (site: string) => DynamicConfigMap | null): JsonObject {
        const pureKeys = new Set<string>();
        for (const site of allSites) {
            const pureConfig = CommonUtil.filterPureClientBindings(getClientDataConfig(site));
            if (!pureConfig) continue;
            for (const [, handlers] of pureConfig.entries()) {
                for (const handler of handlers) {
                    if (handler.iParamFile) pureKeys.add(`${site}:${handler.iParamFile}`);
                }
            }
        }

        const filtered = new JsonObject();
        if (pureKeys.size === 0) return filtered;
        for (const [key, value] of clientDataParamsStore.entries()) {
            if (pureKeys.has(key)) filtered.set(key, value);
        }
        return filtered;
    },

    // #region ClientHandler Data Island (2B)

    /**
     * The one marker a consuming client handler is declared by, shared by the presence probe and the full
     * parse so the two cannot drift apart — note the trailing =", without which the longer
     * data-client-handler-param attribute would also match.
     */
    clientHandlerAttributeMarker: 'data-client-handler="',

    /**
     * True when the HTML declares at least one consuming client handler. Presence-only probe over the SAME
     * marker extractClientHandlerDeclarations parses, for callers that need the fact and not the
     * declarations — the handler-kind profile stamped onto each Scenario (PLAN_HANDLER_FIX §14), which reads
     * unrendered source templates where a full parse would be wasted work.
     */
    declaresClientHandler(html: string | null): boolean {
        return !!html && html.includes(CommonUtil.clientHandlerAttributeMarker);
    },

    /**
     * Extracts EVERY consuming client-handler declaration from a component's rendered HTML (§2B.3), in document
     * order. Each element carrying a data-client-handler="<Name>" attribute is parsed as an atomic pair — the
     * co-located data-client-handler / data-client-handler-param / data-client-data attributes are read from
     * WITHIN that one element's tag (bounded by its <…>) so a declaration's Name never cross-associates with
     * another declaration's param or island key. The input island key is the DECLARED data-client-data value,
     * naming the IClientData preparer this declaration consumes; an ABSENT attribute declares the PURE lane
     * (no island). STRICT validation: a declaration is emitted only when both the Name and the param are
     * present and non-empty — the island key is legitimately empty for a pure declaration and is therefore NOT
     * part of that contract; a malformed declaration is skipped and logged (never invoked on partial data).
     * The param value is returned with its \" HTML-attribute escaping decoded, ready for
     * JsonUtility.parseJsonString. The Component→Name/Param binding is template-borne (read identically by the
     * ConfigClient console sim and the browser DOM); Name→Type resolution is code-registered per language.
     */
    extractClientHandlerDeclarations(html: string): ClientHandlerDeclaration[] {
        const result: ClientHandlerDeclaration[] = [];
        if (!html) return result;
        const handlerMarker = CommonUtil.clientHandlerAttributeMarker;
        const paramMarker = 'data-client-handler-param="';
        const clientDataMarker = 'data-client-data="';
        let pos = 0;
        while (true) {
            const idx = html.indexOf(handlerMarker, pos);
            if (idx < 0) break;
            const tagStart = html.lastIndexOf('<', idx);
            const tagEnd = html.indexOf('>', idx);
            if (tagStart < 0 || tagEnd < 0) break;
            pos = tagEnd + 1;

            const handlerName = CommonUtil.extractTagAttr(html, idx, tagEnd, handlerMarker);
            const param = CommonUtil.extractTagAttr(html, tagStart, tagEnd, paramMarker);

            // STRICT: the Name and the param are one atomic contract — a declaration missing either is
            // malformed; skip (never invoke on partial data) and record it.
            if (!handlerName || !param) {
                Logger.fallback('F-CLH1', `skipped malformed client-handler declaration: handler='${handlerName}' hasParam=${!!param}`);
                continue;
            }

            // The input island key is DECLARED, naming the IClientData preparer whose island this declaration
            // consumes — the same value the binding carries as IType and applyDynamicClientDataHandlers writes
            // the island under. EMPTY (attribute absent) declares the PURE lane: no island is looked up at all,
            // and the AppSite binding's DataSource "Pure" is what authorises the handler to run.
            //
            // ⛔ Do NOT restore the old <HandlerName>Data derivation as a fallback for an absent attribute. That
            // derivation is what made one processor readable from exactly one preparer, which is why a second
            // near-duplicate IClient had to exist just to own a second key. A fallback here would also make a
            // pure declaration silently re-read the island lane's raw whenever the two share a handler name.
            const clientDataKey = CommonUtil.extractTagAttr(html, tagStart, tagEnd, clientDataMarker);

            // The param is a JSON object escaped for its "-delimited HTML attribute (\" for each inner quote);
            // decode back to valid JSON before the caller parses it.
            result.push(new ClientHandlerDeclaration(handlerName, clientDataKey, param.split('\\"').join('"')));
        }
        return result;
    },

    /**
     * Reads the value of attrMarker (e.g. data-client-handler-param=") occurring within the tag window
     * [searchStart, tagEnd), up to the next UNESCAPED double-quote so a value carrying \" (an embedded JSON
     * param) survives intact. Returns "" when the attribute is absent from the window. Value is returned raw
     * (still escaped); the caller decodes as needed.
     */
    extractTagAttr(html: string, searchStart: number, tagEnd: number, attrMarker: string): string {
        const m = html.indexOf(attrMarker, searchStart);
        if (m < 0 || m >= tagEnd) return '';
        const start = m + attrMarker.length;
        let i = start;
        while (i < tagEnd) {
            const c = html[i];
            if (c === '\\') {
                i += 2;
                continue;
            }
            if (c === '"') break;
            i++;
        }
        if (i >= tagEnd) return '';
        return html.substring(start, i);
    },

    /**
     * Builds one embedded client-data island (§2B.4) wrapping rawJson under islandKey — the value of the
     * emitted data-arshu-clientdata attribute. The key is the "<ComponentName>_<IClientDataType>" producer key:
     * each client-data preparer owns an INDEPENDENT island, so a component hosting several preparers emits
     * several islands (the client maps island→preparer and resolves the consuming IClient separately). Carries
     * the raw (skip-filtered) set the client handler filters WITHOUT a server round-trip. Structured JSON
     * (parsed, not scraped) so the client filter stays byte-identical to the server. Emitted only for an
     * Embedded binding.
     */
    buildClientDataIsland(islandKey: string, rawJson: string): string {
        return '<script type="application/json" data-arshu-clientdata="' + islandKey + '">' + rawJson + '</script>';
    },

    /**
     * Builds the concatenated Embedded client-data island block joined into the FINAL page HTML at the end of
     * the top-level merge (server generation). Sources the RAW sets the client-data preparers recorded in
     * dynamicClientDataResults (keyed "<ComponentName>_<IClientDataType>") — the merge records raw only for an
     * Embedded binding, so this map IS exactly the set of islands to emit. Emits ONE island PER preparer, keyed
     * by the full "<ComponentName>_<IClientDataType>" producer key — each client-data binding is independent (a
     * component may host several preparers, each with its own island), sorted by key for cross-engine and
     * cross-language determinism. The island is a SEPARATE DataHtml joined only into the final HTML — never
     * baked into the reusable component template — so the client can reconstruct the clean component and
     * re-attach the island reproducibly. Returns "" when no Embedded raw was recorded.
     */
    buildClientDataIslands(dynamicClientDataResults: JsonObject): string {
        if (!dynamicClientDataResults || dynamicClientDataResults.size === 0) return '';
        const byKey = new Map<string, string>();
        for (const key of dynamicClientDataResults.keys()) {
            const val = dynamicClientDataResults.get(key);
            if (!val || !val.isObject) continue;
            const rawStr = JsonUtility.serializeObject(val.asObject(), false);
            byKey.set(key, CommonUtil.buildClientDataIsland(key, rawStr));
        }
        if (byKey.size === 0) return '';
        const sortedKeys = Array.from(byKey.keys()).sort();
        let result = '';
        for (const k of sortedKeys) result += byKey.get(k);
        return result;
    },

    /**
     * Joins the Embedded client-data islands (see buildClientDataIslands) into the FINAL page HTML. Inserts the
     * island block immediately before the LAST </body> so a full-page render keeps the islands inside the
     * document body (where a browser-delivered page carries them); falls back to a plain append for an HTML
     * fragment that has no body element (the console merge output). Returns the HTML unchanged when no Embedded
     * raw was recorded.
     */
    appendClientDataIslands(html: string, dynamicClientDataResults: JsonObject): string {
        const islands = CommonUtil.buildClientDataIslands(dynamicClientDataResults);
        if (!islands) return html;
        if (!html) return islands;
        const idx = html.toLowerCase().lastIndexOf('</body>');
        if (idx < 0) return html + islands;
        return html.substring(0, idx) + islands + html.substring(idx);
    },

    /**
     * Extracts the raw JSON payload from a page HTML's client-data island for the given COMPONENT name
     * (§2B.5) — the console simulation parses the island from the SAME rendered HTML string the browser parses
     * from its DOM, certifying the browser path by construction. Returns "" when the island is absent.
     */
    extractClientDataIsland(html: string, componentName: string): string {
        if (!html) return '';
        const open = '<script type="application/json" data-arshu-clientdata="' + componentName + '">';
        const idx = html.indexOf(open);
        if (idx < 0) return '';
        const start = idx + open.length;
        const end = html.indexOf('</script>', start);
        if (end < 0) return '';
        return html.substring(start, end);
    },

    /**
     * Removes every client-data island (§2B.4) from a rendered HTML string, returning the HTML without the
     * <script type="application/json" data-arshu-clientdata="..."></script> blocks. The island payload is
     * DATA (raw scenario JSON) and can legitimately contain literal {{...}} inside description text, so any
     * caller that scans the output for UNRESOLVED template placeholders must strip the islands first to avoid
     * false positives. Handler-name agnostic (strips all islands regardless of Name).
     */
    stripClientDataIslands(html: string): string {
        if (!html) return html;
        const openPrefix = '<script type="application/json" data-arshu-clientdata="';
        const close = '</script>';
        let idx = html.indexOf(openPrefix);
        if (idx < 0) return html;
        let result = '';
        let pos = 0;
        while (idx >= 0) {
            const end = html.indexOf(close, idx);
            if (end < 0) break;
            result += html.substring(pos, idx);
            pos = end + close.length;
            idx = html.indexOf(openPrefix, pos);
        }
        result += html.substring(pos);
        return result;
    },

    // #endregion

    shouldSkipHandler(handlerType: string, skipPatterns: string | null): boolean {
        if (!skipPatterns) {
            return false;
        }

        if (skipPatterns === '*') {
            return true;
        }

        const patterns = skipPatterns.split(',');
        const handlerLower = handlerType.toLowerCase();
        for (let i = 0; i < patterns.length; i++) {
            const pattern = patterns[i].trim();
            if (pattern && handlerLower.indexOf(pattern.toLowerCase()) >= 0) {
                return true;
            }
        }
        return false;
    }
};

export const isAlphaNumeric = CommonUtil.isAlphaNumeric.bind(CommonUtil);
export const findMatchingCloseTag = CommonUtil.findMatchingCloseTag.bind(CommonUtil);
export const removeRemainingSlotPlaceholders = CommonUtil.removeRemainingSlotPlaceholders.bind(CommonUtil);
export const replaceCaseInsensitive = CommonUtil.replaceCaseInsensitive.bind(CommonUtil);
export const normalizeFileContent = CommonUtil.normalizeFileContent.bind(CommonUtil);
export const utf16Len = CommonUtil.utf16Len.bind(CommonUtil);
export const parseComponentReusePattern = CommonUtil.parseComponentReusePattern.bind(CommonUtil);

export const AssemblerConfig = {
    DefaultReplaceHtml: '*****',
    DefaultSkipHtml: '[SKIPPED]',
    DefaultSkipJson: '',
    DefaultReplaceTemplateName: 'NotFound',
    DefaultSkipTemplateName: 'Skipped',
    DefaultAppViewPrefix: 'Main',
    MaxRecursionDepth: 7,
    MaxArrayOperationItems: 5,
    GenOperationJson: false
} as const;

export function getKeys(obj: unknown): string[] {
    if (obj instanceof Map) return Array.from(obj.keys());
    if (obj && typeof obj === 'object' && 'keys' in obj && typeof (obj as { keys: unknown }).keys === 'function') return Array.from((obj as { keys(): Iterable<string> }).keys());
    return Object.keys(obj as Record<string, unknown>);
}
